import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import { useTranslation } from "react-i18next";
import { useFetch } from '../../hooks/useFetch';
import axios from 'axios';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import { useState } from 'react';

export interface Osd {
    id?: number;
    origin_id: number;
    header: string;
    title: string;
    is_enabled: boolean;
    cur_data?: string;
}

export interface OsdFormProps {
  origin_id: number;
  onClose: () => void;
}

const get = async (p: any): Promise<Osd[]> => {
    const res = await axios.get<Osd[]>(`osd/?origin_id=${p.origin_id}`);
    return res.data;
}

const post = async (osd0: Osd): Promise<void> => {
    const {id, cur_data, ...osd} = osd0;
    if (id === undefined) {
        const res = await axios.post<Osd[]>(`osd/`, osd);
    } else {
        const res = await axios.patch<Osd[]>(`osd/${id}/`, osd);
    }
}

const OsdForm: React.FC<OsdFormProps> = ({origin_id, onClose}) => {
    const { t } = useTranslation();
    const title = t('OsdForm.Edit_OSD');
    const { data, loading, error: fetchError } = useFetch<Osd[]>(get, [], {origin_id});
    const [submitError, setSubmitError] = useState<string | null>(null);
    
    if (loading) return <CircularProgress />;

    if (fetchError) return (<Alert severity="error" sx={{ mt: 2 }}>{fetchError.message}</Alert>);
    
    const initialValues: Osd = data && data.length > 0 ? data[0] : {
        origin_id,
        header: '',
        title: '',
        is_enabled: false,
        cur_data: '',
    };
    
    return (
        <>
            <Typography variant="h5" component="div">
                {title}
            </Typography>

            {submitError && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitError(null)}>
                    Помилка збереження: {submitError}
                </Alert>
            )}
            
            <Formik
                initialValues={initialValues}
                enableReinitialize={true} // Важно, чтобы форма обновилась, когда придут данные из fetch
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitError(null);
                    try {
                        await post(values);
                        onClose();
                    } catch (error: any) {
                        console.error("Save error", error);
                        setSubmitError(error.response?.data?.message || error.message || "Server Error");
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, handleReset }) => (
                    <Form>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                            <Field
                                name="header"
                                as={TextField}
                                label={t('OsdForm.Header')}
                                fullWidth
                            />
                            
                            <Field
                                name="title"
                                as={TextField}
                                label={t('OsdForm.Title')}
                                fullWidth
                            />

                            <Field
                                name="cur_data"
                                as={TextField}
                                label={t('OsdForm.cur_data')}
                                fullWidth
                                variant="filled"
                                InputProps={{ readOnly: true, }}
                            />

                            <Field name="is_enabled">
                                {({ field }: any) => (
                                    <FormControlLabel
                                        control={<Checkbox {...field} checked={field.value} />}
                                        label={t('OsdForm.Enabled')}
                                    />
                                )}
                            </Field>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                <Button 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={onClose || handleReset}
                                    disabled={isSubmitting}
                                >
                                    {t('Common.Cancel')}
                                </Button>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    color="primary" 
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('Common.Saving') : t('Common.Save')}
                                </Button>
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        
        </>
    );
}

export default OsdForm;